import React, { useState } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { devReset, getProfile, deleteProfil, getStellen, deleteStelle, tutorialZuruecksetzen } from '../api';
import { resetVisited } from '../hooks/useFirstVisit';
import './DevToolbar.css';


/**
 * DevToolbar — nur im Development-Modus sichtbar.
 * Simuliert verschiedene User-Zustände für Tests.
 */
export default function DevToolbar() {
  const [offen, setOffen] = useState(false);
  const [meldung, setMeldung] = useState(null);

  if (process.env.NODE_ENV !== 'development') return null;

  const zeigMeldung = (text) => {
    setMeldung(text);
    setTimeout(() => setMeldung(null), 2500);
  };

  const actions = [
    {
      label: '🆕 Neuer Nutzer simulieren',
      beschr: 'Alle Daten + Firestore löschen → Login-Screen',
      farbe: '#dc2626',
      fn: async () => {
        zeigMeldung('⏳ Lösche alle Daten…');
        try {
          await devReset();
        } catch (e) {
          console.error('[DevTools] Reset fehlgeschlagen:', e);
        }
        localStorage.clear();
        sessionStorage.clear();
        if (auth.currentUser) await signOut(auth);
        window.location.href = '/onboarding';
      },
    },
    {
      label: '🔄 Ausloggen + Reset',
      beschr: 'Logout + Storage löschen',
      farbe: '#be123c',
      fn: async () => {
        await signOut(auth);
        localStorage.clear();
        sessionStorage.clear();
        zeigMeldung('Ausgeloggt + Storage geleert');
        setTimeout(() => window.location.reload(), 800);
      },
    },
    {
      label: '🎓 Tutorial zurücksetzen',
      beschr: 'visited_pages in Firestore leeren → Tutorial erscheint wieder',
      farbe: '#0891b2',
      fn: async () => {
        zeigMeldung('⏳ Setze Tutorial zurück…');
        try {
          await tutorialZuruecksetzen();
          resetVisited();
          zeigMeldung('✅ Tutorial zurückgesetzt — Seite neu laden');
          setTimeout(() => window.location.reload(), 800);
        } catch (e) {
          zeigMeldung(`❌ Fehler: ${e?.response?.status ?? e?.message}`);
        }
      },
    },
    {
      label: '🔔 Onboarding zurücksetzen',
      beschr: 'Modal erscheint wieder',
      farbe: '#d97706',
      fn: () => {
        localStorage.removeItem('onboarding_done');
        zeigMeldung('Onboarding zurückgesetzt');
        window.location.reload();
      },
    },
    {
      label: '📋 Letztes Profil vergessen',
      beschr: 'Alle Profile aus Firestore löschen + Setup erneut erzwingen',
      farbe: '#7c3aed',
      fn: async () => {
        zeigMeldung('⏳ Lösche Profile…');
        try {
          const [profileRes, stellenRes] = await Promise.all([getProfile(), getStellen()]);
          const profiles = profileRes?.data || [];
          const stellen = stellenRes?.data || [];
          await Promise.all([
            ...profiles.map(p => deleteProfil(p.dateiname)),
            ...stellen.map(s => deleteStelle(s.id)),
          ]);
          const teile = [];
          if (profiles.length) teile.push(`${profiles.length} Profil(e)`);
          if (stellen.length) teile.push(`${stellen.length} Stellenanzeige(n)`);
          zeigMeldung(teile.length ? `✅ ${teile.join(' + ')} gelöscht` : 'ℹ️ Nichts vorhanden');
        } catch (e) {
          zeigMeldung(`❌ Fehler beim Löschen: ${e?.response?.status ?? e?.message}`);
          console.error('[DevTools] Löschung fehlgeschlagen:', e);
          return;
        }
        localStorage.clear();
        sessionStorage.clear();
        setTimeout(() => window.location.reload(), 1000);
      },
    },
  ];

  // Aktuellen Zustand anzeigen
  const nutzer = auth.currentUser;
  const status = {
    'User': nutzer ? `${nutzer.email}` : '— nicht eingeloggt —',
    'Verifiziert': nutzer ? (nutzer.emailVerified ? '✅ ja' : '❌ nein') : '—',
    'Provider': nutzer?.providerData?.[0]?.providerId ?? '—',
    'Onboarding': localStorage.getItem('onboarding_done') ? '✅ done' : '⏳ ausstehend',
    'Letztes Profil': localStorage.getItem('last_profile') ?? '—',
  };

  return (
    <div className="dev-toolbar">
      {/* Toggle Button */}
      <button
        className="dev-toggle"
        onClick={() => setOffen(v => !v)}
        title="Dev Tools"
      >
        🛠 DEV
      </button>

      {/* Panel */}
      {offen && (
        <div className="dev-panel">
          <div className="dev-panel-header">Dev Tools</div>

          {/* Status */}
          <div className="dev-status">
            {Object.entries(status).map(([key, val]) => (
              <div key={key} className="dev-status-row">
                <span className="dev-status-key">{key}</span>
                <span className="dev-status-val">{val}</span>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="dev-actions">
            {actions.map(a => (
              <button
                key={a.label}
                className="dev-action-btn"
                style={{ '--accent': a.farbe }}
                onClick={a.fn}
                title={a.beschr}
              >
                <span>{a.label}</span>
                <span className="dev-action-beschr">{a.beschr}</span>
              </button>
            ))}
          </div>

          {/* Hinweis */}
          <div className="dev-hinweis">
            💡 Dev-Modus: E-Mail-Verifizierung ist aktiv.<br />
            "Letztes Profil vergessen" löscht alle Firestore-Daten + Storage (eingeloggt bleiben).
          </div>


          {/* Feedback */}
          {meldung && <div className="dev-meldung">{meldung}</div>}
        </div>
      )}
    </div>
  );
}
